// src/utils/contract.js (최종 수정된 버전)

import { ethers } from 'ethers';
import { 
  getMetaMaskProvider, 
  isTestnetNetwork, 
  getCurrentNetwork,
  WORLDLAND_NETWORKS 
} from './wallet';

// 스마트 컨트랙트 ABI (수수료율 관리 함수 추가)
export const CONTRACT_ABI = [
  // 이벤트들
  "event TradeCreated(uint256 indexed tradeId, address indexed seller, address indexed buyer, uint256 wlcAmount, uint256 krwAmount, uint256 expiresAt, uint256 feePercent)",
  "event PaymentSubmitted(uint256 indexed tradeId, address indexed buyer, string proofHash)",
  "event TradeCompleted(uint256 indexed tradeId, address indexed seller, address indexed buyer, uint256 wlcAmount, uint256 fee)",
  "event TradeCancelled(uint256 indexed tradeId, address indexed canceller, string reason)",
  "event FeePercentChanged(uint256 oldFeePercent, uint256 newFeePercent, address changedBy)",
  
  // 읽기 함수들
  "function getTrade(uint256 _tradeId) view returns (tuple(uint256 tradeId, address seller, address buyer, uint256 wlcAmount, uint256 krwAmount, uint256 expiresAt, uint8 status, string paymentProofHash, uint256 createdAt, bool sellerConfirmed, uint256 feePercent))",
  "function getTradeStatus(uint256 _tradeId) view returns (uint8)",
  "function isTradeExpired(uint256 _tradeId) view returns (bool)",
  "function getUserTradeCount(address _user) view returns (uint256)",
  "function getContractBalance() view returns (uint256)",
  "function nextTradeId() view returns (uint256)",
  "function totalFees() view returns (uint256)",
  "function owner() view returns (address)",
  "function getCurrentFeePercent() view returns (uint256)",
  "function getCurrentFeePercentage() view returns (string)",
  "function getTradeFeePercent(uint256 _tradeId) view returns (uint256)",
  "function feePercent() view returns (uint256)",
  
  // 쓰기 함수들 (반환값 포함)
  "function createTrade(address _buyer, uint256 _krwAmount, uint256 _durationHours) payable returns (uint256)",
  "function submitPaymentProof(uint256 _tradeId, string _proofHash)",
  "function confirmPayment(uint256 _tradeId)",
  "function cancelTrade(uint256 _tradeId, string _reason)",
  
  // 관리자 함수들
  "function withdrawFees()",
  "function emergencyCancel(uint256 _tradeId, string _reason)",
  "function transferOwnership(address _newOwner)",
  "function setFeePercent(uint256 _newFeePercent)"
];

// 컨트랙트 주소 (환경변수 지원)
export const CONTRACT_ADDRESSES = {
  mainnet: process.env.REACT_APP_CONTRACT_ADDRESS_MAINNET || "0xc44d3ec358353c3f4639bdfd0f40b936f2c56d0a",
  testnet: process.env.REACT_APP_CONTRACT_ADDRESS_TESTNET || "0x0000000000000000000000000000000000000000", 
  sepolia: process.env.REACT_APP_CONTRACT_ADDRESS_SEPOLIA || "0x0000000000000000000000000000000000000000"
};

// 기본 수수료율 (환경변수에서 로드)
export const DEFAULT_FEE_PERCENT = (() => {
  const envValue = process.env.REACT_APP_DEFAULT_FEE_PERCENT;
  console.log('Environment REACT_APP_DEFAULT_FEE_PERCENT:', envValue);
  
  if (envValue === undefined || envValue === null || envValue === '') {
    console.log('환경변수가 설정되지 않음, 기본값 100 사용');
    return 100; // 1%
  }
  
  const parsed = parseInt(envValue, 10);
  if (isNaN(parsed)) {
    console.log('환경변수 파싱 실패, 기본값 100 사용');
    return 100; // 1%
  }
  
  console.log('환경변수에서 로드된 수수료율:', parsed);
  return parsed;
})();

// 수수료율 변환 유틸리티
export const feePercentToPercentage = (feePercent) => {
  return (feePercent / 100).toFixed(2) + '%';
};

export const percentageToFeePercent = (percentage) => {
  return Math.round(parseFloat(percentage) * 100);
};

// 거래 상태 열거형
export const TRADE_STATUS = {
  CREATED: 0,
  PAYMENT_SUBMITTED: 1,
  COMPLETED: 2,
  CANCELLED: 3
};

// 거래 상태 이름
export const TRADE_STATUS_NAMES = {
  0: "생성됨",
  1: "송금증명 제출됨",
  2: "완료됨",
  3: "취소됨"
};

// 현재 네트워크에 맞는 컨트랙트 주소 가져오기
export const getCurrentContractAddress = async () => {
  const { getCurrentNetwork, WORLDLAND_NETWORKS } = await import('./wallet');
  const currentChainId = await getCurrentNetwork();
  
  let address;
  if (currentChainId === WORLDLAND_NETWORKS.mainnet.chainId) {
    address = CONTRACT_ADDRESSES.mainnet;
  } else if (currentChainId === WORLDLAND_NETWORKS.testnet.chainId) {
    address = CONTRACT_ADDRESSES.testnet;
  } else if (currentChainId === WORLDLAND_NETWORKS.sepolia.chainId) {
    address = CONTRACT_ADDRESSES.sepolia;
  } else {
    throw new Error('지원되지 않는 네트워크입니다.');
  }
  
  if (address === "0x0000000000000000000000000000000000000000") {
    throw new Error('현재 네트워크에 컨트랙트가 배포되지 않았습니다.');
  }
  
  return address;
};

// 컨트랙트 인스턴스 가져오기
export const getContract = async () => {
  const provider = getMetaMaskProvider();
  
  if (!provider) {
    throw new Error('MetaMask가 연결되지 않았습니다.');
  }
  
  const contractAddress = await getCurrentContractAddress();
  const ethersProvider = new ethers.BrowserProvider(provider);
  const signer = await ethersProvider.getSigner();
  
  return new ethers.Contract(contractAddress, CONTRACT_ABI, signer);
};

// 읽기 전용 컨트랙트 인스턴스
export const getReadOnlyContract = async () => {
  const provider = getMetaMaskProvider();
  
  if (!provider) {
    throw new Error('MetaMask가 연결되지 않았습니다.');
  }
  
  const contractAddress = await getCurrentContractAddress();
  const ethersProvider = new ethers.BrowserProvider(provider);
  
  return new ethers.Contract(contractAddress, CONTRACT_ABI, ethersProvider);
};

// Wei를 WLC로 변환
export const weiToWLC = (weiAmount) => {
  return ethers.formatEther(weiAmount);
};

// WLC를 Wei로 변환
export const wlcToWei = (wlcAmount) => {
  return ethers.parseEther(wlcAmount.toString());
};

// 수수료율 설정 (관리자용) - 새로 추가
export const setFeePercent = async (newFeePercent) => {
  try {
    const contract = await getContract();
    const tx = await contract.setFeePercent(newFeePercent);
    await tx.wait();
    return true;
  } catch (error) {
    console.error('수수료율 설정 실패:', error);
    throw new Error('수수료율 설정에 실패했습니다: ' + error.message);
  }
};

// 현재 수수료율 조회
export const getCurrentFeePercent = async () => {
  try {
    const contract = await getReadOnlyContract();
    const feePercent = await contract.getCurrentFeePercent();
    return Number(feePercent);
  } catch (error) {
    console.error('수수료율 조회 실패:', error);
    // 실패 시 기본값 반환
    return DEFAULT_FEE_PERCENT;
  }
};

// 수수료율을 백분율 문자열로 조회
export const getCurrentFeePercentage = async () => {
  try {
    const contract = await getReadOnlyContract();
    const percentage = await contract.getCurrentFeePercentage();
    return percentage;
  } catch (error) {
    console.error('수수료율 백분율 조회 실패:', error);
    // 실패 시 기본값 계산해서 반환
    return feePercentToPercentage(DEFAULT_FEE_PERCENT);
  }
};

// 특정 거래의 수수료율 조회
export const getTradeFeePercent = async (tradeId) => {
  try {
    const contract = await getReadOnlyContract();
    const feePercent = await contract.getTradeFeePercent(tradeId);
    return Number(feePercent);
  } catch (error) {
    console.error('거래 수수료율 조회 실패:', error);
    throw new Error('거래 수수료율 조회에 실패했습니다: ' + error.message);
  }
};

// 거래 생성 (수정된 버전)
export const createTrade = async (buyerAddress, wlcAmount, krwAmount, durationHours) => {
  try {
    console.log('거래 생성 시작:', { buyerAddress, wlcAmount, krwAmount, durationHours });
    
    const contract = await getContract();
    const wlcInWei = wlcToWei(wlcAmount);
    
    console.log('컨트랙트 호출 중...', {
      buyerAddress,
      krwAmount,
      durationHours,
      wlcInWei: wlcInWei.toString()
    });
    
    // createTrade 함수 호출 (반환값 받기)
    const tx = await contract.createTrade(
      buyerAddress,
      krwAmount,
      durationHours,
      { 
        value: wlcInWei,
        gasLimit: 300000  // 가스 한도 명시적 설정
      }
    );
    
    console.log('트랜잭션 전송됨:', tx.hash);
    
    // 트랜잭션 대기 및 영수증 받기
    const receipt = await tx.wait();
    console.log('트랜잭션 영수증:', receipt);
    
    // 방법 1: 이벤트에서 거래 ID 추출
    let tradeId = null;
    
    if (receipt.logs && receipt.logs.length > 0) {
      const contractInterface = new ethers.Interface(CONTRACT_ABI);
      
      for (const log of receipt.logs) {
        try {
          const contractAddress = await getCurrentContractAddress();
          if (log.address.toLowerCase() !== contractAddress.toLowerCase()) {
            continue;
          }
          
          const parsedLog = contractInterface.parseLog({
            topics: log.topics,
            data: log.data
          });
          
          if (parsedLog && parsedLog.name === 'TradeCreated') {
            tradeId = parsedLog.args.tradeId.toString();
            console.log('이벤트에서 거래 ID 발견:', tradeId);
            break;
          }
        } catch (parseError) {
          console.log('로그 파싱 실패:', parseError);
        }
      }
    }
    
    // 방법 2: 정적 호출로 반환값 확인 (fallback)
    if (!tradeId) {
      try {
        console.log('정적 호출로 거래 ID 추정 시도');
        
        // nextTradeId에서 1을 뺀 값이 방금 생성된 거래의 ID
        const readOnlyContract = await getReadOnlyContract();
        const nextId = await readOnlyContract.nextTradeId();
        const estimatedTradeId = (Number(nextId) - 1).toString();
        
        // 해당 거래가 실제로 존재하고 현재 사용자가 판매자인지 확인
        const tradeInfo = await readOnlyContract.getTrade(estimatedTradeId);
        const [currentAccount] = await window.ethereum.request({ method: 'eth_accounts' });
        
        if (tradeInfo.seller.toLowerCase() === currentAccount.toLowerCase()) {
          tradeId = estimatedTradeId;
          console.log('정적 호출로 거래 ID 확인:', tradeId);
        }
      } catch (staticError) {
        console.error('정적 호출 실패:', staticError);
      }
    }
    
    // 방법 3: 컨트랙트 호출 결과에서 직접 추출 (새로운 시도)
    if (!tradeId) {
      try {
        console.log('컨트랙트 호출 결과 분석 시도');
        
        // 동일한 파라미터로 정적 호출하여 반환값 확인
        // 주의: 이 방법은 실제 거래를 생성하지 않고 시뮬레이션만 함
        const readOnlyContract = await getReadOnlyContract();
        
        // staticCall로 반환값 시뮬레이션
        const simulatedResult = await readOnlyContract.createTrade.staticCall(
          buyerAddress,
          krwAmount,
          durationHours,
          { value: wlcInWei }
        );
        
        // 시뮬레이션 결과가 실제 거래 ID일 가능성이 높음
        if (simulatedResult) {
          const potentialTradeId = simulatedResult.toString();
          
          // 실제로 해당 ID의 거래가 존재하는지 확인
          try {
            const trade = await readOnlyContract.getTrade(potentialTradeId);
            if (trade.seller.toLowerCase() === (await window.ethereum.request({ method: 'eth_accounts' }))[0].toLowerCase()) {
              tradeId = potentialTradeId;
              console.log('정적 호출 시뮬레이션으로 거래 ID 확인:', tradeId);
            }
          } catch (verifyError) {
            console.log('거래 ID 검증 실패:', verifyError);
          }
        }
      } catch (simulationError) {
        console.log('정적 호출 시뮬레이션 실패:', simulationError);
      }
    }
    
    if (!tradeId) {
      // 최후의 수단: 사용자에게 알림
      console.log('모든 거래 ID 감지 방법 실패');
      throw new Error(`거래가 성공적으로 생성되었습니다!\n\n트랜잭션 해시: ${tx.hash}\n\n거래 목록 탭에서 새로고침하여 거래 ID를 확인해주세요.`);
    }
    
    console.log('최종 거래 ID:', tradeId);
    return tradeId;
    
  } catch (error) {
    console.error('거래 생성 실패:', error);
    
    // 사용자 친화적인 에러 메시지
    if (error.code === 4001) {
      throw new Error('사용자가 트랜잭션을 거부했습니다.');
    } else if (error.code === -32603) {
      throw new Error('트랜잭션 실행에 실패했습니다. 가스비가 부족하거나 컨트랙트 오류일 수 있습니다.');
    } else if (error.message.includes('insufficient funds')) {
      throw new Error('잔액이 부족합니다. WLC 잔액과 가스비를 확인해주세요.');
    } else if (error.message.includes('execution reverted')) {
      throw new Error('스마트 컨트랙트 실행이 실패했습니다. 입력값을 확인해주세요.');
    } else if (error.message.includes('트랜잭션 해시:')) {
      // 거래는 성공했지만 ID 감지 실패인 경우
      throw error; // 그대로 전달
    }
    
    throw new Error('거래 생성에 실패했습니다: ' + error.message);
  }
};

// 송금 증명 제출
export const submitPaymentProof = async (tradeId, proofHash) => {
  try {
    const contract = await getContract();
    const tx = await contract.submitPaymentProof(tradeId, proofHash);
    await tx.wait();
    return true;
  } catch (error) {
    console.error('송금 증명 제출 실패:', error);
    throw new Error('송금 증명 제출에 실패했습니다: ' + error.message);
  }
};

// 거래 승인
export const confirmPayment = async (tradeId) => {
  try {
    const contract = await getContract();
    const tx = await contract.confirmPayment(tradeId);
    await tx.wait();
    return true;
  } catch (error) {
    console.error('거래 승인 실패:', error);
    throw new Error('거래 승인에 실패했습니다: ' + error.message);
  }
};

// 거래 취소
export const cancelTrade = async (tradeId, reason = "사용자 요청") => {
  try {
    const contract = await getContract();
    const tx = await contract.cancelTrade(tradeId, reason);
    await tx.wait();
    return true;
  } catch (error) {
    console.error('거래 취소 실패:', error);
    throw new Error('거래 취소에 실패했습니다: ' + error.message);
  }
};

// 수수료 출금 (관리자용) - 새로 추가
export const withdrawFees = async () => {
  try {
    const contract = await getContract();
    const tx = await contract.withdrawFees();
    await tx.wait();
    return true;
  } catch (error) {
    console.error('수수료 출금 실패:', error);
    throw new Error('수수료 출금에 실패했습니다: ' + error.message);
  }
};

// 긴급 취소 (관리자용) - 새로 추가
export const emergencyCancel = async (tradeId, reason = "관리자 긴급 취소") => {
  try {
    const contract = await getContract();
    const tx = await contract.emergencyCancel(tradeId, reason);
    await tx.wait();
    return true;
  } catch (error) {
    console.error('긴급 취소 실패:', error);
    throw new Error('긴급 취소에 실패했습니다: ' + error.message);
  }
};

// 소유권 이전 (관리자용) - 새로 추가
export const transferOwnership = async (newOwner) => {
  try {
    const contract = await getContract();
    const tx = await contract.transferOwnership(newOwner);
    await tx.wait();
    return true;
  } catch (error) {
    console.error('소유권 이전 실패:', error);
    throw new Error('소유권 이전에 실패했습니다: ' + error.message);
  }
};

// 거래 정보 조회
export const getTrade = async (tradeId) => {
  try {
    console.log('거래 상세 조회:', tradeId);
    const contract = await getReadOnlyContract();
    const trade = await contract.getTrade(tradeId);
    
    console.log('원시 거래 데이터:', trade);
    
    const result = {
      tradeId: trade.tradeId.toString(),
      seller: trade.seller,
      buyer: trade.buyer,
      wlcAmount: weiToWLC(trade.wlcAmount),
      krwAmount: trade.krwAmount.toString(),
      expiresAt: new Date(Number(trade.expiresAt) * 1000),
      status: trade.status,
      statusName: TRADE_STATUS_NAMES[trade.status],
      paymentProofHash: trade.paymentProofHash,
      createdAt: new Date(Number(trade.createdAt) * 1000),
      sellerConfirmed: trade.sellerConfirmed,
      feePercent: Number(trade.feePercent) // 수수료율 추가
    };
    
    console.log('파싱된 거래 데이터:', result);
    return result;
  } catch (error) {
    console.error('거래 조회 실패:', error);
    throw new Error('거래 조회에 실패했습니다: ' + error.message);
  }
};

// 사용자별 거래 조회 (이벤트 기반)
export const getUserTrades = async (userAddress) => {
  try {
    console.log('거래 목록 조회 시작:', userAddress);
    
    const contract = await getReadOnlyContract();
    const contractAddress = await getCurrentContractAddress();
    
    console.log('컨트랙트 주소:', contractAddress);
    console.log('현재 네트워크:', await getCurrentNetwork());
    
    const provider = getMetaMaskProvider();
    if (!provider) {
      throw new Error('MetaMask 프로바이더를 찾을 수 없습니다.');
    }
    
    const ethersProvider = new ethers.BrowserProvider(provider);
    const currentBlock = await ethersProvider.getBlockNumber();
    console.log('현재 블록 번호:', currentBlock);
    
    // 더 넓은 범위에서 이벤트 조회
    const fromBlock = Math.max(0, currentBlock - 10000);
    
    console.log('이벤트 조회 범위:', fromBlock, '~', currentBlock);
    
    // 모든 거래 이벤트 조회
    const allTradesFilter = contract.filters.TradeCreated();
    const allEvents = await contract.queryFilter(allTradesFilter, fromBlock, currentBlock);
    console.log('전체 거래 이벤트 수:', allEvents.length);
    
    // 사용자 관련 거래만 필터링
    const userEvents = allEvents.filter(event => {
      const seller = event.args.seller.toLowerCase();
      const buyer = event.args.buyer.toLowerCase();
      const user = userAddress.toLowerCase();
      return seller === user || buyer === user;
    });
    
    console.log('사용자 관련 거래 수:', userEvents.length);
    
    if (userEvents.length === 0) {
      console.log('거래 이벤트를 찾을 수 없습니다.');
      return [];
    }
    
    // 거래 정보 가져오기
    console.log('거래 상세 정보 조회 중...');
    const trades = await Promise.all(
      userEvents.map(async (event, index) => {
        try {
          console.log(`거래 ${index + 1}/${userEvents.length} 조회 중... ID: ${event.args.tradeId}`);
          const trade = await getTrade(event.args.tradeId);
          return trade;
        } catch (error) {
          console.error(`거래 ID ${event.args.tradeId} 조회 실패:`, error);
          return null;
        }
      })
    );
    
    // null 값 제거 및 최신순 정렬
    const validTrades = trades.filter(trade => trade !== null);
    const sortedTrades = validTrades.sort((a, b) => b.createdAt - a.createdAt);
    
    console.log('최종 거래 목록:', sortedTrades);
    return sortedTrades;
    
  } catch (error) {
    console.error('사용자 거래 목록 조회 실패:', error);
    throw new Error('거래 목록 조회에 실패했습니다: ' + error.message);
  }
};

// 파일 해시 계산
export const calculateFileHash = async (file) => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};